import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Footer from '../../home/components/Footer';
import { movingTipsArticles, ArticleBlock } from '../../../mocks/movingTipsArticles';

function renderBlock(block: ArticleBlock, index: number) {
  switch (block.type) {
    case 'heading':
      return (
        <h2 key={index} id={`section-${index}`} className="text-2xl font-bold text-gray-900 mt-8 mb-4">
          {block.text}
        </h2>
      );
    case 'paragraph':
      return (
        <p key={index} className="text-gray-700 leading-relaxed">
          {block.text}
        </p>
      );
    case 'list':
      return (
        <ul key={index} className="list-disc pl-6 space-y-2 text-gray-700">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case 'highlight':
      return (
        <p key={index} className="text-xl font-bold text-yellow-600 my-2">
          {block.text}
        </p>
      );
    case 'internalLink':
      return (
        <div key={index} className="my-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
          <p className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wide">Related Guide</p>
          <a
            href={`/moving-tips/article?id=${block.articleId}`}
            className="text-gray-900 font-semibold hover:text-yellow-600 transition-colors text-base leading-snug"
          >
            <i className="ri-article-line mr-2 text-yellow-500"></i>
            {block.linkText}
          </a>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticlePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const articleId = new URLSearchParams(location.search).get('id');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith('#')) {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Packing Tips': 'bg-yellow-400 text-gray-900',
      'Moving Checklist': 'bg-blue-500 text-white',
      'Family Moving': 'bg-green-500 text-white',
      'Eco Tips': 'bg-emerald-500 text-white',
      'Planning': 'bg-purple-500 text-white',
      'Organization': 'bg-orange-500 text-white',
      'Cost Analysis': 'bg-red-500 text-white'
    };
    return colors[category] || 'bg-gray-500 text-white';
  };

  // Find the article
  const article = movingTipsArticles.find(a => a.id.toString() === articleId);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/moving-tips" className="text-yellow-500 hover:text-yellow-600 font-semibold">
            Back to Moving Tips
          </Link>
        </div>
      </div>
    );
  }

  // Get related articles — use explicit relatedIds if available, otherwise fallback to same category
  const relatedArticles = (() => {
    if (article.relatedIds && article.relatedIds.length > 0) {
      return article.relatedIds
        .map(id => movingTipsArticles.find(a => a.id === id))
        .filter(Boolean)
        .slice(0, 3) as typeof movingTipsArticles;
    }
    return movingTipsArticles
      .filter(a => a.id !== article.id && a.category === article.category)
      .concat(movingTipsArticles.filter(a => a.id !== article.id && a.category !== article.category))
      .slice(0, 3);
  })();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || menuOpen ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className={`max-w-7xl mx-auto px-6 flex items-center justify-between transition-all duration-300 ${isScrolled ? 'py-2' : 'py-4'}`}>
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/0cdc8a6b92ed12030230ca2c206db970.png"
              alt="BoxFreeMove"
              className={`w-auto transition-all duration-300 ${isScrolled ? 'h-12' : 'h-28'}`}
            />
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-800 transition-colors hover:text-yellow-400">Home</Link>
            <a href="/#how-it-works" onClick={() => handleNavClick('#how-it-works')} className="text-sm font-medium text-gray-800 transition-colors hover:text-yellow-400 cursor-pointer">How It Works</a>
            <a href="/#pricing" onClick={() => handleNavClick('#pricing')} className="text-sm font-medium text-gray-800 transition-colors hover:text-yellow-400 cursor-pointer">Pricing</a>
            <Link to="/moving-tips" className="text-sm font-medium text-yellow-500 transition-colors hover:text-yellow-400">Moving Tips</Link>
            <a href="/#faq" onClick={() => handleNavClick('#faq')} className="text-sm font-medium text-gray-800 transition-colors hover:text-yellow-400 cursor-pointer">FAQ</a>
            <a href="/#reserve" onClick={() => handleNavClick('#reserve')} className="bg-yellow-400 text-gray-900 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer">
              Reserve Now
            </a>
          </div>

          {/* Hamburger button (mobile only) */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${menuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>

        {/* Mobile dropdown menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              Home
            </Link>
            <a
              href="/#how-it-works"
              onClick={() => handleNavClick('#how-it-works')}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              How It Works
            </a>
            <a
              href="/#pricing"
              onClick={() => handleNavClick('#pricing')}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              Pricing
            </a>
            <Link
              to="/moving-tips"
              onClick={() => setMenuOpen(false)}
              className="text-yellow-500 font-medium text-sm hover:text-yellow-400 transition-colors cursor-pointer"
            >
              Moving Tips
            </Link>
            <a
              href="/#faq"
              onClick={() => handleNavClick('#faq')}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              FAQ
            </a>
            <a
              href="/#reserve"
              onClick={() => handleNavClick('#reserve')}
              className="bg-yellow-400 text-gray-900 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer w-full text-center"
            >
              Reserve Now
            </a>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <div className="pt-32 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/moving-tips" className="inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-600 font-semibold text-sm mb-6 cursor-pointer">
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Back to Moving Tips
          </Link>
          <div className="mb-4">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getCategoryColor(article.category)}`}>
              {article.category}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">{article.title}</h1>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              <span>{article.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Article Cover Image */}
      <div className="max-w-5xl mx-auto px-6 mb-12">
        <div className="relative w-full h-96 rounded-2xl overflow-hidden shadow-lg">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Article Content */}
      <article className="flex-1 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="prose prose-lg max-w-none space-y-6">
            {article.content.map((block, index) => renderBlock(block, index))}
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-yellow-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Make Your Move Easier?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Skip the cardboard hassle and rent sturdy, stackable moving totes delivered right to your door.
          </p>
          <a
            href="/#reserve"
            onClick={() => handleNavClick('#reserve')}
            className="inline-block bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
          >
            Reserve Your Totes Now
          </a>
        </div>
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedArticles.map((relatedArticle) => (
                <Link
                  key={relatedArticle.id}
                  to={`/moving-tips/article?id=${relatedArticle.id}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                >
                  <div className="relative w-full h-48 bg-gray-100">
                    <img
                      src={relatedArticle.coverImage}
                      alt={relatedArticle.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getCategoryColor(relatedArticle.category)}`}>
                        {relatedArticle.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                      <span>{relatedArticle.readTime}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight hover:text-yellow-500 transition-colors">
                      {relatedArticle.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {relatedArticle.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}